import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useLocation,
	useRouteMatch,
} from 'react-router-dom';
import { Button, Dimmer, Header, Message } from 'semantic-ui-react';
import { Logo } from '../Logo/Logo';
import { LoginForm } from '../LoginForm/LoginForm';
import { SimpleFormWrapper } from '../SimpleFormWrapper/SimpleFormWrapper';
import { VerificationCodeForm } from '../VerificationCodeForm/VerificationCodeForm';
import { UserDetailsForm } from '../UserDetailsForm/UserDetailsForm';
import { ChoosePasswordForm } from '../ChoosePasswordForm/ChoosePasswordForm';
import { OrganisationNameForm } from '../OrganisationNameForm/OrganisationNameForm';
import { OrganisationDomainForm } from '../OrganisationDomainForm/OrganisationDomainForm';
import { ProtectedRoute } from '../ProtectedRoute/ProtectedRoute';
import { Spinner } from '../Spinner/Spinner';
import { AuthState } from '../../types/auth-state';
import { AppState } from '../../types/app-state';
import { RegisterState } from '../../types/register-state';
import {
	CheckExistingDomain,
	CheckExistingEmail,
	ConfirmVerificationCode,
	Destroy,
	GetVerificationCode,
	Initialize,
	RegisterOrganisation,
	RegisterUser,
	SetOrganisationDetails,
	SetUserDetails,
} from '../../actions/register.actions';
import { Login } from '../../actions/auth.actions';
import './RegistrationPage.less';

const nextButton = (step: number, disableSubmit: boolean = false) => (
	<div className='step-button-wrapper'>
		<Button fluid type='submit' primary disabled={disableSubmit}>
			Next
		</Button>
	</div>
);

export const RegistrationPage: React.FC = () => {
	const location = useLocation();
	const history = useHistory();
	const match = useRouteMatch();
	const dispatch = useDispatch();
	const { sysadmin: isAdmin, token } = useSelector<
		AppState,
		Partial<AuthState>
	>((state) => state.auth);
	const {
		emailExisting: requireLogin,
		checkingEmail: showEmailLoading,
		checkingDomain: showDomainLoading,
		domainExisting: domainUnavailable,
		email,
		loading,
		message,
		codeVerified,
		organisationName,
		// organisationDomain,
		organisationRegisteredId,
		// complete,
	} = useSelector<AppState, Partial<RegisterState>>((state) => state.register);
	const isAuthenticated = !!token;
	const step = +(location.pathname.match(/\d$/) || [0])[0];
	const wrapperPosition = 'middle';

	const handleEmailChange = (e: React.ChangeEvent<any>) => {
		dispatch(CheckExistingEmail(e.target.value));
	};

	const handleDomainChange = (e: React.ChangeEvent<any>) => {
		if (/^[a-zA-Z0-9-]{3,}$/.test(e.target.value.trim())) {
			dispatch(CheckExistingDomain(e.target.value.toLowerCase().trim()));
		}
	};

	const nextStep = (values: any, actions: any) => {
		const step = +(location.pathname.match(/\d$/) || [0])[0];

		if (step === 1) {
			if (requireLogin) {
				dispatch(Login(values.email, values.password));
			} else {
				dispatch(GetVerificationCode(values.email));
			}
			return;
		}

		if (step === 2) {
			dispatch(ConfirmVerificationCode(values.code));
			return;
		}

		if (step === 3) {
			dispatch(SetUserDetails({ ...values }));
			history.push(`/register/4`);
			return;
		}

		if (step === 4) {
			dispatch(SetUserDetails({ ...values }));
			dispatch(RegisterUser());
			return;
		}

		if (step === 5) {
			dispatch(SetOrganisationDetails({ ...values }));
			history.push(`/register/6`);
			return;
		}

		if (step === 6) {
			dispatch(SetOrganisationDetails({ ...values }));
			dispatch(RegisterOrganisation());
		}
	};

	useEffect(() => {
		dispatch(Initialize());
		return () => {
			dispatch(Destroy());
		};
	}, [dispatch]);

	useEffect(() => {
		const step = +(location.pathname.match(/\d$/) || [0])[0];

		if (step < 5 && isAuthenticated) {
			if (isAdmin) {
				history.push(`/register/5`);
			} else {
				history.push(`/`);
			}
			return;
		}

		if (step === 1) {
			if (email) {
				history.push(`/register/2`);
				return;
			}
		}

		if (step === 2) {
			if (!email) {
				history.push(`/register/1`);
			} else if (codeVerified) {
				history.push(`/register/3`);
				return;
			}
		}

		if (step > 2 && step < 5) {
			if (!codeVerified) {
				history.push(`/register/2`);
				return;
			}
		}

		if (step === 6) {
			if (!organisationName) {
				history.push(`/register/5`);
				return;
			}
			if (organisationRegisteredId) {
				history.push(`/register/7`);
				return;
			}
		}
	}, [
		history,
		location.pathname,
		isAuthenticated,
		isAdmin,
		email,
		codeVerified,
		organisationName,
		organisationRegisteredId,
	]);

	return (
		<div className='registration-page'>
			<Dimmer inverted active={loading}>
				<Spinner />
			</Dimmer>
			<SimpleFormWrapper className={wrapperPosition}>
				<Logo />
				{message && (
					<Message negative>
						<p>{message}</p>
					</Message>
				)}
				<Switch>
					<Route path={match.url + '/1'}>
						<LoginForm
							onSubmit={nextStep}
							onEmailChange={handleEmailChange}
							showPassword={!!requireLogin}
							showEmailLoading={!!showEmailLoading}
							renderActionButtons={(disableSubmit) =>
								nextButton(step, showEmailLoading || disableSubmit)
							}
						>
							<Header as='h3'>Register your organisation</Header>
							{requireLogin && (
								<p>
									Welcome back! Please login to register your new organisation.
								</p>
							)}
						</LoginForm>
					</Route>
					<Route path={match.url + '/2'}>
						<VerificationCodeForm
							onSubmit={nextStep}
							renderActionButtons={(disableSubmit) =>
								nextButton(step, disableSubmit)
							}
						/>
					</Route>
					<Route path={match.url + '/3'}>
						<UserDetailsForm
							onSubmit={nextStep}
							renderActionButtons={(disableSubmit) =>
								nextButton(step, disableSubmit)
							}
						/>
					</Route>
					<Route path={match.url + '/4'}>
						<ChoosePasswordForm
							onSubmit={nextStep}
							renderActionButtons={(disableSubmit) =>
								nextButton(step, disableSubmit)
							}
						/>
					</Route>
					<ProtectedRoute path={match.url + '/5'} adminOnly={true}>
						<OrganisationNameForm
							onSubmit={nextStep}
							renderActionButtons={(disableSubmit) =>
								nextButton(step, disableSubmit)
							}
						/>
					</ProtectedRoute>
					<ProtectedRoute path={match.url + '/6'} adminOnly={true}>
						<OrganisationDomainForm
							onDomainChange={handleDomainChange}
							loading={!!showDomainLoading}
							onSubmit={nextStep}
							unavailable={domainUnavailable}
							renderActionButtons={(disableSubmit) =>
								nextButton(step, domainUnavailable || disableSubmit)
							}
						/>
					</ProtectedRoute>
					<Route path={match.url + '/7'}>
						<Redirect
							to={'/admin/org/invite?orgId=' + organisationRegisteredId}
						/>
					</Route>
					<Redirect to={'/'} />
				</Switch>
			</SimpleFormWrapper>
		</div>
	);
};

/*
export class RegistrationPage extends React.Component<RegistrationPageWithRouteProps> {

  handleEmailChange = (e: React.ChangeEvent<any>) => {
    if (this.props.checkEmail) {
      this.props.checkEmail(e.target.value);
    }
  }

  handleDomainChange = (e: React.ChangeEvent<any>) => {
    if (this.props.checkDomain && /^[a-zA-Z0-9-]{3,}$/.test(e.target.value.trim())) {
      this.props.checkDomain(e.target.value.toLowerCase().trim());
    }
  }

  nextStep = (values: any, actions: any) => {
    const { 
      confirmVerificationCode,
      getVerificationCode,
      history,
      location, 
      login,
      registerOrganisation,
      registerUser,
      requireLogin,
      setOrganisationDetails,
      setUserDetails
    } = this.props;
    const step = +(location.pathname.match(/\d$/) || [0])[0];

    if (step === 1) {
      if (requireLogin) {
        login(values.email, values.password);
      } else {
        getVerificationCode(values.email);
      }
      return;
    }

    if (step === 2) {
      confirmVerificationCode(values.code);
      return;
    }

    if (step === 3) {
      setUserDetails({ ...values });
      history.push(`/register/4`);
      return;
    }

    if (step === 4) {
      setUserDetails({ ...values });
      registerUser();
      return;
    }

    if (step === 5) {
      setOrganisationDetails({ ...values });
      history.push(`/register/6`);
      return;
    }

    if (step === 6) {
      setOrganisationDetails({ ...values });
      registerOrganisation();
    }

  }

  validateStep = (props: RegistrationPageWithRouteProps) => {
    const { 
      email,
      codeVerified,
      // complete,
      history, 
      isAuthenticated,
      location,
      organisationName,
      organisationRegisteredId,
      isAdmin
    } = props;
    const step = +(location.pathname.match(/\d$/) || [0])[0];

    if (step < 5 && isAuthenticated) {
      if (isAdmin) {
        history.push(`/register/5`);
      } else {
        history.push(`/`);
      }
      return;
    }

    if (step === 1) {
      if (email) {
        history.push(`/register/2`);
        return;
      }
    }

    if (step === 2) {
      if (!email) {
        history.push(`/register/1`);
      } else if (codeVerified) {
        history.push(`/register/3`);
        return;
      }
    }

    if (step > 2 && step < 5) {
      if (!codeVerified) {
        history.push(`/register/2`);
        return;
      }
    }

    if (step === 6) {
      if (!organisationName) {
        history.push(`/register/5`);
        return;
      }
      if (organisationRegisteredId) {
        history.push(`/register/7`);
        return;
      }
    }
  }

  componentWillReceiveProps(nextProps: RegistrationPageWithRouteProps) {
    this.validateStep(nextProps);
  }

  componentDidMount() {
    if (this.props.init) {
      this.props.init();
    }
    this.validateStep(this.props);
  }

  componentWillUnmount() {
    if (this.props.destroy) {
      this.props.destroy();
    }
  }

  render() {
    const { 
      location,
      loading,
      match, 
      message,
      requireLogin,
      showEmailLoading,
      showDomainLoading,
      domainUnavailable,
      organisationRegisteredId
    } = this.props;
    const step = +(location.pathname.match(/\d$/) || [0])[0];
    const wrapperPosition = 'middle'; // step > 4 ? 'right' : (step > 2 ? 'middle' : 'left');

    return (
      <div className="registration-page">
        <Dimmer inverted active={loading}><Spinner/></Dimmer>
        <SimpleFormWrapper className={wrapperPosition}>
          <Logo />
          {message && <Message negative><p>{message}</p></Message>}
          <Switch>
            <Route path={match.url + '/1'}>
              <LoginForm 
                onSubmit={this.nextStep}
                onEmailChange={this.handleEmailChange}
                showPassword={!!requireLogin}
                showEmailLoading={!!showEmailLoading}
                renderActionButtons={(disableSubmit) => (nextButton(step, showEmailLoading || disableSubmit))}
              >
                <Header as="h3">Register your organisation</Header>
                {requireLogin && <p>
                  Welcome back! Please login to register your new organisation.
                </p>}
              </LoginForm>
            </Route>
            <Route path={match.url + '/2'}>
              <VerificationCodeForm
                onSubmit={this.nextStep}
                renderActionButtons={(disableSubmit) => (nextButton(step, disableSubmit))}
              />
            </Route>
            <Route path={match.url + '/3'}>
              <UserDetailsForm
                onSubmit={this.nextStep}
                renderActionButtons={(disableSubmit) => (nextButton(step, disableSubmit))}
              />
            </Route>
            <Route path={match.url + '/4'}>
              <ChoosePasswordForm
                onSubmit={this.nextStep}
                renderActionButtons={(disableSubmit) => (nextButton(step, disableSubmit))}
              />
            </Route>
            <ProtectedRoute path={match.url + '/5'} adminOnly={true}>
              <OrganisationNameForm
                onSubmit={this.nextStep}
                renderActionButtons={(disableSubmit) => (nextButton(step, disableSubmit))}
              />
            </ProtectedRoute>
            <ProtectedRoute path={match.url + '/6'} adminOnly={true}>
              <OrganisationDomainForm
                onDomainChange={this.handleDomainChange}
                loading={!!showDomainLoading}
                onSubmit={this.nextStep}
                unavailable={domainUnavailable}
                renderActionButtons={(disableSubmit) => (nextButton(step, domainUnavailable || disableSubmit))}
              />
            </ProtectedRoute>
            <Route path={match.url + '/7'}>
              <Redirect to={'/admin/org/invite?orgId=' + organisationRegisteredId} />
            </Route>
            <Redirect to={'/'} />
          </Switch>
        </SimpleFormWrapper>
      </div>
    );
  }
}
*/
